import { ZIN_DISPLAY_KEY_REELS_VILLA_PAGE, ZIN_KEY } from "@website/accommodation-ui/constant";
import { HotelBTSCoverage } from "@website/accommodation-ui/hotel-bts-coverage";
import { HotelCalendar } from "@website/accommodation-ui/hotel-calendar";
import { HotelReserveBookingCard } from "@website/accommodation-ui/hotel-reserve-booking-card";
import { HotelReserveDetails } from "@website/accommodation-ui/hotel-reserve-details";
import { HotelShortVideos } from "@website/accommodation-ui/hotel-short-videos";
import { VillaReserveCancellationPolicy } from "@website/accommodation-ui/villa-reserve-cancellation-policies";
import { VillaSpaces } from "@website/accommodation-ui/villa-spaces";
import { VillaSpecifications } from "@website/accommodation-ui/villa-specifications";
import { VillaSimilarRoomsInform } from "@website/accommodation-ui/villa_similar-rooms_inform/villa_similar_rooms_inform";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import { Breadcrumb } from "@website/ui/breadcrumb";
import { CollapsibleText } from "@website/ui/collapsible-text";
import { FabMenu } from "@website/ui/fab-menu";
import { getOrigin } from "@website/utils/channel/get-origin";
import { isChannel } from "@website/utils/channel/helpers";
import { Footer } from "@website/widget/footer";
import { Header } from "@website/widget/header";
import { ReserveCommentsLayout } from "@website/widget/reserve-comments-layout";
import { ReserveFacilities } from "@website/widget/reserve-facilities";
import { ReserveGallery } from "@website/widget/reserve-gallery";
import { ReserveHostPolicies } from "@website/widget/reserve-host-policies";
import { ReserveMap } from "@website/widget/reserve-map";
import { ReserveNearBy } from "@website/widget/reserve-nearby";
import { ReservePolicies } from "@website/widget/reserve-policies";
import { ReserveRate } from "@website/widget/reserve-rate";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import Custom404 from "../../[...gone].page";
import { useHotelPageDataTransform } from "./index.hook";
import { HotelPagePropertiesType } from "./index.types";
const Index: NextPage<HotelPagePropertiesType> = properties => {
  const router = useRouter();
  if (!properties.roomInfo) {
    return <Custom404 />;
  }
  const {
    booking,
    hotelBreadcrumbsProps,
    hotelReserveFacilitiesProps,
    hotelReserveMapProps,
    gallery,
    hotelReserveDetailsProps,
    hotelReserveCancellationPoliciesProps,
    hotelReserveRateProps,
    hotelReserveCommentsProps,
    description,
    hotelReservePoliciesProps,
    hotelReserveHostPoliciesProps,
    calendarProps,
    hotelReserveBtsCoveragesProps,
    villaSpecifications,
    villaSimilarRoomsProps,
    hotelReserveNearByProps,
    villaSpaces,
    isAnyPolicyExist
  } = useHotelPageDataTransform(properties);
  const fabItems = [{
    title: "تصاویر اقامتگاه",
    selector: "#hotel_gallery"
  }, {
    title: "مشخصات",
    selector: "#hotel_specifications"
  }, {
    title: "امکانات",
    selector: "#hotel_facilities"
  }, ...(villaSpaces.length > 0 && villaSpaces?.every(item => item?.amenitiesGroup?.length > 0) ? [{
    title: "فضاهای اقامتگاه",
    selector: "#hotel_spaces"
  }] : []), {
    title: "تقویم",
    selector: "#hotel_calendar"
  }, {
    title: "قوانین",
    selector: "#hotel_policies"
  }, ...(hotelReserveCommentsProps?.rate?.rate || hotelReserveCommentsProps?.comments?.length > 0 ? [{
    title: "امتیاز و نظرات",
    selector: "#hotel_comments"
  }] : []), ...(hotelReserveMapProps && hotelReserveMapProps?.circle?.lat !== 35.696_814 && hotelReserveMapProps?.circle?.lng !== 51.349_818_6 ? [{
    title: "موقعیت",
    selector: "#hotel_location"
  }] : [])];
  return <>
      <Head>
        <title>{`${hotelReserveDetailsProps.hotelTitle} | اسنپ تریپ`}</title>
        {properties.roomInfo?.seo?.noIndex && <meta content="noindex,nofollow" name="robots" />}
        <link rel="canonical" href={`${getOrigin()}${process.env.NEXT_PUBLIC_BASE_PATHNAME}/room/${router.query?.roomId}`} />
      </Head>
      <Header title="اقامتگاه" />
      <FabMenu items={fabItems} eventToTracking={addEventToTracking?.bind(null, USER_TRACKING_EVENTS.VILLA_SPACES_SECTION_VIEWED, {
      product: "villa"
    })} />
      <section>
        <article className="relative flex flex-col">
          <div className="px-0 md:px-[24px] xl:px-[calc((100%-1200px)/2)]">
            <div className="hidden py-[22px] md:block">
              <Breadcrumb {...hotelBreadcrumbsProps} className="hidden lg:flex" />
            </div>
            <ReserveGallery gallery={gallery} title="گالری تصاویر اقامتگاه" />
            {isChannel(["jek", "android"]) && <HotelShortVideos className={"px-6 lg:m-0 lg:hidden"} displayType={"REELS"} displayId={ZIN_DISPLAY_KEY_REELS_VILLA_PAGE} zinKey={ZIN_KEY} zinTags={`${router.query?.roomId}`} />}
          </div>
          <div className="px-[24px] pb-[90px] xl:px-[calc((100%-1200px)/2)]">
            <div className="relative grid-cols-3 gap-6 lg:grid">
              <div className="col-span-2">
                <div className="flex flex-col gap-[56px]">
                  <HotelReserveDetails {...hotelReserveDetailsProps} />
                  <VillaSpecifications villaSpecifications={villaSpecifications} />
                  <ReserveFacilities {...hotelReserveFacilitiesProps} />
                  <VillaSimilarRoomsInform {...villaSimilarRoomsProps} />
                  <HotelBTSCoverage btsCoverage={hotelReserveBtsCoveragesProps} />
                  <VillaSpaces spaces={villaSpaces} />
                  {description && <div>
                      <h6 className="text-on-surface-high-emphasis text-headline-6 mb-5">
                        اقامتگاه
                      </h6>
                      <CollapsibleText ariaLabel="description" collapsedClassName="h-[50px]" content={<p className="whitespace-pre-wrap">{description}</p>} />
                    </div>}
                  <HotelCalendar {...calendarProps} showTitle />
                  <ReservePolicies {...hotelReservePoliciesProps} />
                  <ReserveHostPolicies {...hotelReserveHostPoliciesProps} />
                  {isAnyPolicyExist ? <VillaReserveCancellationPolicy {...hotelReserveCancellationPoliciesProps} /> : null}

                  <ReserveRate {...hotelReserveRateProps} />
                  <ReserveCommentsLayout {...hotelReserveCommentsProps} />
                </div>
              </div>
              <div className="top-14 z-[5] col-span-1 mt-6 h-fit lg:sticky">
                <HotelReserveBookingCard {...booking} />
              </div>
            </div>
            {hotelReserveMapProps && <ReserveMap {...hotelReserveMapProps} />}
            {hotelReserveNearByProps && <ReserveNearBy {...hotelReserveNearByProps} />}
          </div>
        </article>
      </section>
      <Footer />
    </>;
};
export default Index;
export { getServerSideProps } from "./index.server";